/*
 * Copyright '2024' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { timeout } from "./timeout";
const fixedBackoff = (attempt, delay) => delay;
const linearBackoff = (attempt, delay) => attempt * delay;
const exponentialBackoff = (attempt, delay) => Math.pow(delay, attempt);
export const defaultRetryConfig = {
    backoff: "FIXED",
    delay: 100,
    logger: () => undefined,
    maxBackOff: 5 * 60 * 1000,
    retries: 10,
    timeout: 60 * 1000,
    until: () => true,
    retryIf: () => true
};
export async function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export async function retry(f, config) {
    const effectiveConfig = Object.assign({}, defaultRetryConfig, config);
    return timeout(effectiveConfig.timeout, (done) => _retry(f, effectiveConfig, done));
}
export function retryDecorator(func, config) {
    return (...args) => retry(() => func(...args), config);
}
export function customizeDecorator(customConfig) {
    return (args, config) => retryDecorator(args, Object.assign({}, customConfig, config));
}
// tslint:disable-next-line
export function customizeRetry(customConfig) {
    return (f, c) => {
        const customized = Object.assign({}, customConfig, c);
        return retry(f, customized);
    };
}
async function _retry(f, config, done) {
    let lastError;
    let delay;
    switch (config.backoff) {
        case "EXPONENTIAL":
            delay = exponentialBackoff;
            break;
        case "FIXED":
            delay = fixedBackoff;
            break;
        case "LINEAR":
            delay = linearBackoff;
            break;
        default:
            delay = config.backoff;
    }
    let retries;
    if (config.retries === "INFINITELY") {
        retries = Number.MAX_SAFE_INTEGER;
    }
    else {
        retries = config.retries;
    }
    for (let i = 0; i <= retries; i++) {
        try {
            const result = await f();
            if (config.until(result)) {
                return result;
            }
            config.logger("Until condition not met by " + result);
        }
        catch (error) {
            if (!config.retryIf(error)) {
                throw error;
            }
            if (error.name === NotRetryableError.name) {
                throw new RetryError(`Met not retryable error. Last error: ${error}`, error);
            }
            lastError = error;
            config.logger("Retry failed: " + error.message);
        }
        const millisToWait = delay(i + 1, config.delay);
        await wait(millisToWait > config.maxBackOff ? config.maxBackOff : millisToWait);
        if (done()) {
            break;
        }
    }
    throw new RetryError(`All retries failed. Last error: ${lastError}`, lastError);
}
export const notEmpty = (result) => {
    if (Array.isArray(result)) {
        return result.length > 0;
    }
    return result !== null && result !== undefined;
};
export class RetryError extends Error {
    /*  istanbul ignore next  */
    constructor(message, lastError) {
        super(message);
        this.lastError = lastError;
    }
}
// tslint:disable-next-line:max-classes-per-file
class BaseError {
    constructor(...args) {
        return Error.apply(this, args);
    }
}
// tslint:disable-next-line:max-classes-per-file
export class NotRetryableError extends BaseError {
    constructor(message) {
        super(message);
        Object.defineProperty(this, 'name', { value: this.constructor.name });
    }
}
//# sourceMappingURL=data:application/json;base64,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